import { BehaviorSubject, filter } from 'rxjs';
import { DestroyRef, inject, Injectable } from '@angular/core';
import {
  QRWebsocketEvents,
  QRWebsocketPayload,
  QRWebsocketService,
} from './qr-websocket.service';
import { DOCUMENT } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

/**
 * Shape mirrors the auris `GuestViewAppearance` model — both fields optional,
 * primary missing/null means "no brand colour configured".
 */
export interface GuestBrand {
  primary?: string | null;
  secondary?: string | null;
}

export interface GuestViewAppearance {
  theme?: 'light' | 'dark' | null;
  brand?: GuestBrand | null;
}

/**
 * CSS custom properties the service maintains on the document root. The header
 * SCSS reads these via `var(--brand-primary, …)` with sensible fallbacks so the
 * existing chrome shows through when no brand colour is configured.
 */
const CSS_VAR_BRAND_PRIMARY = '--brand-primary';
const CSS_VAR_BRAND_ON = '--brand-on-brand';

/**
 * Owns guest-side spectator appearance (MIC-312): subscribes to
 * GUEST_VIEW_APPEARANCE_UPDATED on the guest websocket and writes the resolved
 * brand colour onto the document root as CSS custom properties so the spectator
 * chrome (currently the header) can pick it up declaratively.
 *
 * The proxy pushes this event at guest connect time and again whenever the
 * owner changes the brand colour mid-session.
 */
@Injectable({ providedIn: 'root' })
export class GuestAppearanceService {
  private readonly document = inject(DOCUMENT);
  private readonly destroyRef = inject(DestroyRef);
  private readonly ws = inject(QRWebsocketService);

  private readonly _brandPrimary = new BehaviorSubject<string | null>(null);
  /** Current brand-primary hex (lowercase, normalized to #RRGGBB) or null when unset. */
  readonly brandPrimary$ = this._brandPrimary.asObservable();

  private started = false;

  /**
   * Hooks the WS event stream. Idempotent — calling start() more than once is a
   * no-op. Safe to call eagerly at app bootstrap.
   *
   * Unlike the device-level appearance flow, we subscribe unconditionally rather
   * than gating on auth mode here: on this SPA the guest joins after bootstrap
   * (no page reload), so an auth-mode check at start() time would make the
   * service permanently inert. The guest websocket (QRWebsocketService, opened
   * with client_type=guest) only ever carries guest events, so an unconditional
   * subscription cannot interfere with a regular logged-in user.
   */
  start(): void {
    if (this.started) return;
    this.started = true;

    this.ws.incomingMessages
      .pipe(
        filter((m) => m.event === QRWebsocketEvents.GUEST_VIEW_APPEARANCE_UPDATED),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((message) => this.apply(message));
  }

  private apply(message: QRWebsocketPayload): void {
    const appearance = (message.payload ?? {}) as GuestViewAppearance;
    const primary = this.normalize(appearance.brand?.primary ?? null);
    this._brandPrimary.next(primary);

    const root = this.document.documentElement;
    if (!root) return;

    if (primary) {
      root.style.setProperty(CSS_VAR_BRAND_PRIMARY, primary);
      root.style.setProperty(CSS_VAR_BRAND_ON, this.textOnBrand(primary));
    } else {
      // Explicit clear so removing a brand colour mid-session reverts to the
      // default chrome rather than leaving the previous colour stuck.
      root.style.removeProperty(CSS_VAR_BRAND_PRIMARY);
      root.style.removeProperty(CSS_VAR_BRAND_ON);
    }
  }

  /**
   * Returns '#000000' or '#ffffff' depending on which gives better contrast
   * against the given hex background (WCAG relative-luminance threshold).
   */
  private textOnBrand(hex: string): string {
    const c = hex.replace('#', '');
    if (c.length !== 6) return '#000000';
    const r = parseInt(c.substring(0, 2), 16) / 255;
    const g = parseInt(c.substring(2, 4), 16) / 255;
    const b = parseInt(c.substring(4, 6), 16) / 255;
    const linearize = (v: number) =>
      v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
    const luminance =
      0.2126 * linearize(r) + 0.7152 * linearize(g) + 0.0722 * linearize(b);
    return luminance > 0.179 ? '#000000' : '#ffffff';
  }

  /**
   * The backend stores normalized lowercase 6-digit hex, but be defensive about
   * whatever shows up on the wire — accept #RGB / #RGBA / #RRGGBB / #RRGGBBAA,
   * always emit #RRGGBB lowercase.
   */
  private normalize(value: string | null): string | null {
    if (!value) return null;
    const v = value.toLowerCase();
    if (/^#[0-9a-f]{6}$/.test(v)) return v;
    if (/^#[0-9a-f]{8}$/.test(v)) return v.slice(0, 7);
    if (/^#[0-9a-f]{3}$/.test(v)) {
      return '#' + v[1] + v[1] + v[2] + v[2] + v[3] + v[3];
    }
    if (/^#[0-9a-f]{4}$/.test(v)) {
      return '#' + v[1] + v[1] + v[2] + v[2] + v[3] + v[3];
    }
    return null;
  }
}
